import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
} from 'recharts';
import {
  AlertTriangle,
  Bell,
  CalendarClock,
  CheckCircle2,
  Clock,
  Lightbulb,
  Mail,
  PenSquare,
  Plug,
  Sparkles,
  UserPlus,
  X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { ContentIdea, NotificationType } from '../../shared/types';
import { PLATFORM_LIST, platformName } from '../../shared/platforms';
import { PageHeader } from '../components/layout';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  PlatformIcon,
  Skeleton,
  SkeletonText,
  useToast,
} from '../components/ui';
import { PostDetailDrawer } from '../components/posts/PostDetailDrawer';
import { api, ApiError } from '../lib/api';
import { qk } from '../lib/query';
import { formatDay, formatRelative, formatWhen } from '../lib/format';
import { useSessionStore } from '../stores/session';
import { useActiveWorkspace, useWorkspaceStore, useWorkspaceTimezone } from '../stores/workspace';
import { cn } from '../lib/utils';

const SPARKLINE_DAYS = 14;
const UPCOMING_WINDOW_DAYS = 30;

/** "2026-08-26" → the same key shifted by `delta` days, in plain UTC arithmetic. */
function shiftDayKey(key: string, delta: number): string {
  const [year, month, day] = key.split('-').map(Number) as [number, number, number];
  const shifted = new Date(Date.UTC(year, month - 1, day + delta));
  return `${shifted.getUTCFullYear()}-${String(shifted.getUTCMonth() + 1).padStart(2, '0')}-${String(shifted.getUTCDate()).padStart(2, '0')}`;
}

function greetingFor(hour: number): string {
  if (hour < 5) return 'Still up';
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

const ACTIVITY_ICONS: Partial<Record<NotificationType, LucideIcon>> = {
  publish_success: CheckCircle2,
  publish_failure: AlertTriangle,
  post_scheduled: Clock,
  token_expiring: AlertTriangle,
  token_expired: AlertTriangle,
  invite_sent: Mail,
  invite_accepted: UserPlus,
  member_joined: UserPlus,
  style_analyzed: Sparkles,
};

const ACTIVITY_TONES: Partial<Record<NotificationType, string>> = {
  publish_success: 'text-success',
  publish_failure: 'text-danger',
  token_expiring: 'text-warning',
  token_expired: 'text-danger',
  style_analyzed: 'text-accent',
};

/**
 * A failed fetch must never be dressed as an empty workspace — "Nothing
 * scheduled" when the request 500'd sends people looking for a post they
 * definitely made. Every card on this page distinguishes the two.
 */
function LoadFailure({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center gap-2 py-4 text-center">
      <AlertTriangle className="size-5 text-warning" aria-hidden />
      <p className="text-sm text-fg">Could not load this yet</p>
      <p className="text-xs text-muted">
        {error instanceof ApiError ? error.message : 'Something went wrong on the way here.'}
      </p>
      <Button variant="secondary" size="xs" className="mt-1" onClick={onRetry}>
        Try again
      </Button>
    </div>
  );
}

/**
 * Dashboard — owned by WEB-3. Greeting, quick actions, an idea generator,
 * upcoming schedule, recent activity, connection health and a 14-day
 * publishing sparkline.
 */
export function DashboardPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const user = useSessionStore((state) => state.user);
  const workspaceId = useWorkspaceStore((state) => state.workspaceId);
  const workspace = useActiveWorkspace();
  const timezone = useWorkspaceTimezone();
  const [selectedPostId, setSelectedPostId] = useState<string | null>(null);

  const nowHour = Number(
    new Intl.DateTimeFormat('en-US', { timeZone: timezone, hour: 'numeric', hourCycle: 'h23' }).format(
      new Date(),
    ),
  );
  const greeting = greetingFor(Number.isFinite(nowHour) ? nowHour : new Date().getHours());
  const firstName = user?.name?.split(' ')[0]?.trim() || user?.email?.split('@')[0] || 'there';

  /* ---------------- ideas ---------------- */
  const ideas = useMutation({
    mutationFn: () => api.ai.ideas({ count: 4 }),
    onError: (error) => {
      toast({
        title: 'Could not generate ideas',
        description: error instanceof ApiError ? error.message : undefined,
        tone: 'error',
      });
    },
  });

  const draftIdea = (idea: ContentIdea) => {
    const topic = idea.title;
    navigate(`/composer?topic=${encodeURIComponent(topic)}`, {
      state: {
        prefillTopic: topic,
        prefillAngle: idea.angle,
        prefillHook: idea.hook,
        prefillPlatforms: idea.suggestedPlatforms,
      },
    });
  };

  /* ---------------- upcoming scheduled ---------------- */
  const upcomingRange = useMemo(() => {
    const from = new Date();
    const to = new Date(from.getTime() + UPCOMING_WINDOW_DAYS * 86_400_000);
    return { from: from.toISOString(), to: to.toISOString() };
  }, []);

  const upcomingQuery = useQuery({
    queryKey: qk.calendar(workspaceId, upcomingRange.from, upcomingRange.to),
    queryFn: () => api.calendar.range(upcomingRange),
    enabled: Boolean(workspaceId),
  });

  const upcoming = (upcomingQuery.data?.items ?? [])
    .filter((item) => item.status === 'scheduled' && item.scheduledAt)
    .sort((a, b) => (a.scheduledAt ?? 0) - (b.scheduledAt ?? 0))
    .slice(0, 6);

  /* ---------------- recent activity (notifications) ---------------- */
  const activityQuery = useQuery({
    queryKey: [...qk.notifications(workspaceId, false), 'dashboard'],
    queryFn: () => api.notifications.list({ limit: 6 }),
    enabled: Boolean(workspaceId),
  });
  const activity = activityQuery.data?.notifications ?? [];

  /* ---------------- connection status ---------------- */
  const accountsQuery = useQuery({
    queryKey: qk.accounts(workspaceId),
    queryFn: () => api.accounts.list(),
    enabled: Boolean(workspaceId),
  });
  const platformStatus = accountsQuery.data?.platforms ?? [];

  /* ---------------- mini stats + sparkline ---------------- */
  const postsQuery = useQuery({
    queryKey: [...qk.posts(workspaceId, {}), 'dashboard-recent'],
    queryFn: () => api.posts.list({ limit: 100 }),
    enabled: Boolean(workspaceId),
  });
  const recentPosts = postsQuery.data?.posts ?? [];

  const stats = useMemo(() => {
    const monthKeyPrefix = new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit' })
      .format(new Date())
      .slice(0, 7); // "YYYY-MM"

    const inThisMonth = (ms: number) =>
      new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit' })
        .format(new Date(ms))
        .slice(0, 7) === monthKeyPrefix;

    let postsThisMonth = 0;
    let publishedThisMonth = 0;
    for (const post of recentPosts) {
      if (inThisMonth(post.createdAt)) postsThisMonth += 1;
      for (const entry of post.platforms) {
        if (entry.status === 'published' && entry.publishedAt && inThisMonth(entry.publishedAt)) {
          publishedThisMonth += 1;
        }
      }
    }
    return { postsThisMonth, publishedThisMonth };
  }, [recentPosts, timezone]);

  const sparklineData = useMemo(() => {
    const dayKeyOf = (ms: number) =>
      new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(
        new Date(ms),
      );
    const todayKey = dayKeyOf(Date.now());
    const counts = new Map<string, number>();
    for (const post of recentPosts) {
      for (const entry of post.platforms) {
        if (entry.status === 'published' && entry.publishedAt) {
          const key = dayKeyOf(entry.publishedAt);
          counts.set(key, (counts.get(key) ?? 0) + 1);
        }
      }
    }
    return Array.from({ length: SPARKLINE_DAYS }, (_, index) => {
      const key = shiftDayKey(todayKey, -(SPARKLINE_DAYS - 1 - index));
      const [year, month, day] = key.split('-').map(Number) as [number, number, number];
      return {
        key,
        label: formatDay(new Date(year, month - 1, day)),
        count: counts.get(key) ?? 0,
      };
    });
  }, [recentPosts, timezone]);

  const hasAnyPublishedRecently = sparklineData.some((point) => point.count > 0);

  return (
    <>
      <PageHeader
        title={`${greeting}, ${firstName}`}
        description={`Here's what's happening in ${workspace?.name ?? 'your workspace'}.`}
        actions={
          <>
            <Button
              variant="secondary"
              leftIcon={<Sparkles className="size-4" />}
              loading={ideas.isPending}
              onClick={() => ideas.mutate()}
            >
              {ideas.data ? 'Regenerate ideas' : 'Generate ideas'}
            </Button>
            <Button leftIcon={<PenSquare className="size-4" />} onClick={() => navigate('/composer')}>
              New post
            </Button>
          </>
        }
      />

      {(ideas.isPending || ideas.data) && (
        <Card className="mb-6">
          <CardHeader className="flex-row items-center justify-between space-y-0">
            <div className="flex items-center gap-2">
              <Lightbulb className="size-4 text-accent" aria-hidden />
              <CardTitle>Ideas to draft</CardTitle>
              {ideas.data?.provider === 'mock' && (
                <Badge tone="warning" size="sm">
                  Demo AI
                </Badge>
              )}
            </div>
            <Button variant="ghost" size="icon-sm" aria-label="Dismiss ideas" onClick={() => ideas.reset()}>
              <X className="size-4" />
            </Button>
          </CardHeader>
          <CardContent>
            {ideas.isPending ? (
              <div className="grid gap-3 sm:grid-cols-2">
                {Array.from({ length: 4 }).map((_, index) => (
                  <div key={index} className="space-y-2 rounded-lg border border-border p-3">
                    <Skeleton className="h-4 w-3/4" />
                    <SkeletonText lines={2} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {ideas.data?.ideas.map((idea, index) => (
                  <div key={index} className="flex flex-col gap-2 rounded-lg border border-border p-3">
                    <p className="text-sm font-medium text-fg">{idea.title}</p>
                    <p className="text-xs text-muted">{idea.hook}</p>
                    <div className="flex flex-wrap items-center gap-1.5 pt-1">
                      {idea.suggestedPlatforms.map((platform) => (
                        <PlatformIcon key={platform} platform={platform} size="xs" variant="soft" />
                      ))}
                    </div>
                    <Button
                      size="sm"
                      variant="soft"
                      className="mt-1 self-start"
                      onClick={() => draftIdea(idea)}
                    >
                      Draft this
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader className="flex-row items-center justify-between space-y-0">
              <CardTitle>Publishing activity</CardTitle>
              <span className="text-xs text-subtle">Last {SPARKLINE_DAYS} days</span>
            </CardHeader>
            <CardContent>
              <div className="mb-4 flex flex-wrap items-end gap-8">
                <div>
                  <p className="pap-tnum text-2xl font-semibold text-fg">
                    {postsQuery.isLoading ? (
                      <Skeleton className="h-7 w-10" />
                    ) : postsQuery.isError ? (
                      '—'
                    ) : (
                      stats.postsThisMonth
                    )}
                  </p>
                  <p className="text-xs text-subtle">Posts this month</p>
                </div>
                <div>
                  <p className="pap-tnum text-2xl font-semibold text-fg">
                    {postsQuery.isLoading ? (
                      <Skeleton className="h-7 w-10" />
                    ) : postsQuery.isError ? (
                      '—'
                    ) : (
                      stats.publishedThisMonth
                    )}
                  </p>
                  <p className="text-xs text-subtle">Published this month</p>
                </div>
              </div>
              {postsQuery.isLoading ? (
                <Skeleton className="h-24 w-full" />
              ) : postsQuery.isError ? (
                <LoadFailure error={postsQuery.error} onRetry={() => void postsQuery.refetch()} />
              ) : !hasAnyPublishedRecently ? (
                <p className="flex h-24 items-center justify-center text-xs text-subtle">
                  Publish a post and this fills in.
                </p>
              ) : (
                <div className="h-24">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={sparklineData} margin={{ top: 6, right: 4, bottom: 0, left: 4 }}>
                      <defs>
                        <linearGradient id="dashboardSparkline" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="var(--color-accent)" stopOpacity={0.35} />
                          <stop offset="100%" stopColor="var(--color-accent)" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="label" hide />
                      <RechartsTooltip
                        cursor={{ stroke: 'var(--color-border-strong)', strokeWidth: 1 }}
                        contentStyle={{
                          background: 'var(--color-elevated)',
                          border: '1px solid var(--color-border)',
                          borderRadius: 8,
                          fontSize: 12,
                          padding: '6px 10px',
                        }}
                        labelStyle={{ color: 'var(--color-subtle)', marginBottom: 2 }}
                        itemStyle={{ color: 'var(--color-fg)' }}
                        formatter={(value) => [`${value} published`, '']}
                      />
                      <Area
                        type="monotone"
                        dataKey="count"
                        stroke="var(--color-accent)"
                        strokeWidth={2}
                        fill="url(#dashboardSparkline)"
                        dot={false}
                        activeDot={{ r: 3 }}
                        isAnimationActive={false}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex-row items-center justify-between space-y-0">
              <div className="flex items-center gap-2">
                <CalendarClock className="size-4 text-accent" aria-hidden />
                <CardTitle>Upcoming</CardTitle>
              </div>
              <Button variant="link" size="xs" onClick={() => navigate('/calendar')}>
                View calendar
              </Button>
            </CardHeader>
            <CardContent>
              {upcomingQuery.isLoading ? (
                <SkeletonText lines={4} />
              ) : upcomingQuery.isError ? (
                <LoadFailure
                  error={upcomingQuery.error}
                  onRetry={() => void upcomingQuery.refetch()}
                />
              ) : upcoming.length === 0 ? (
                <EmptyState
                  variant="plain"
                  icon={<CalendarClock className="size-5" />}
                  title="Nothing scheduled"
                  description="Schedule a post and it will show up here."
                  action={
                    <Button size="sm" onClick={() => navigate('/composer')}>
                      Schedule a post
                    </Button>
                  }
                />
              ) : (
                <ul className="divide-y divide-border">
                  {upcoming.map((item) => (
                    <li key={item.platformPostId}>
                      <button
                        type="button"
                        onClick={() => setSelectedPostId(item.postId)}
                        className="flex w-full items-center gap-3 py-2.5 text-left transition-colors hover:bg-elevated/40"
                      >
                        <PlatformIcon platform={item.platform} size="sm" variant="soft" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm text-fg">
                            {item.title?.trim() || item.excerpt || platformName(item.platform)}
                          </span>
                          <span className="block text-xs text-subtle">
                            {formatWhen(item.scheduledAt, timezone)}
                          </span>
                        </span>
                        {item.simulated && (
                          <Badge tone="info" size="sm">
                            Simulated
                          </Badge>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex-row items-center gap-2 space-y-0">
              <Bell className="size-4 text-accent" aria-hidden />
              <CardTitle>Recent activity</CardTitle>
            </CardHeader>
            <CardContent>
              {activityQuery.isLoading ? (
                <SkeletonText lines={4} />
              ) : activityQuery.isError ? (
                <LoadFailure
                  error={activityQuery.error}
                  onRetry={() => void activityQuery.refetch()}
                />
              ) : activity.length === 0 ? (
                <p className="py-4 text-center text-sm text-muted">
                  Publishing results and team activity will show up here.
                </p>
              ) : (
                <ul className="divide-y divide-border">
                  {activity.map((notification) => {
                    const Icon = ACTIVITY_ICONS[notification.type] ?? Bell;
                    return (
                      <li key={notification.id} className="flex items-start gap-3 py-2.5">
                        <Icon
                          className={cn('mt-0.5 size-4 shrink-0', ACTIVITY_TONES[notification.type] ?? 'text-subtle')}
                          aria-hidden
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm text-fg">{notification.title}</span>
                          {notification.body && (
                            <span className="pap-clamp-2 block text-xs leading-relaxed text-muted">
                              {notification.body}
                            </span>
                          )}
                        </span>
                        <span className="shrink-0 text-xs text-subtle">
                          {formatRelative(notification.createdAt)}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader className="flex-row items-center gap-2 space-y-0">
              <Plug className="size-4 text-accent" aria-hidden />
              <CardTitle>Connections</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {accountsQuery.isLoading ? (
                <div className="space-y-2">
                  {Array.from({ length: 5 }).map((_, index) => (
                    <Skeleton key={index} className="h-12 w-full" />
                  ))}
                </div>
              ) : accountsQuery.isError ? (
                <LoadFailure
                  error={accountsQuery.error}
                  onRetry={() => void accountsQuery.refetch()}
                />
              ) : (
                (platformStatus.length ? platformStatus : PLATFORM_LIST.map((spec) => ({
                  platform: spec.id,
                  configured: false,
                  comingSoon: spec.comingSoon,
                  canSimulate: false,
                  connectedCount: 0,
                }))).map((status) => (
                  <div
                    key={status.platform}
                    className="flex items-center gap-3 rounded-lg border border-border px-3 py-2.5"
                  >
                    <PlatformIcon
                      platform={status.platform}
                      size="sm"
                      variant={status.connectedCount > 0 ? 'solid' : 'soft'}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-fg">
                        {platformName(status.platform)}
                      </p>
                      <p className="text-xs text-subtle">
                        {status.comingSoon
                          ? 'Coming soon'
                          : status.connectedCount > 0
                            ? `${status.connectedCount} connected`
                            : 'Not connected'}
                      </p>
                    </div>
                    <Button
                      size="xs"
                      variant={status.connectedCount > 0 ? 'secondary' : 'soft'}
                      disabled={status.comingSoon}
                      onClick={() => navigate('/connections')}
                    >
                      {status.comingSoon ? 'Soon' : status.connectedCount > 0 ? 'Manage' : 'Connect'}
                    </Button>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <PostDetailDrawer postId={selectedPostId} onClose={() => setSelectedPostId(null)} />
    </>
  );
}

export default DashboardPage;
